import { ProductBuilderState } from '../state';
import { ProductParameter, ProductParameterValue, productParameterValues, Product } from '../../product';
import { ProductBuilderAction } from '.';
import { call, put, takeEvery } from 'redux-saga/effects';
import { ProductParameterValueConstraints, ProductConfiguration } from '../../product/model';

export const productBuilderParameterChanged = (state: ProductBuilderState, params: { productParameterKey: string, productParameterChanged: boolean; }): ProductBuilderState => {
    const parameters = [...state.productParameters];
    const parameter = parameters.find(it => it.key === params.productParameterKey);
    parameter.changed = params.productParameterChanged;
    return {
        ...state,
        productParameters: parameters
    };
};

export const productBuilderParameterExpand = (state: ProductBuilderState, params: { productParameter: ProductParameter, productParameterValues: ProductParameterValue[]; }): ProductBuilderState => {
    const newState: ProductBuilderState = {
        ...state,
        productParameterExpandedKey: state.productParameterExpandedKey === params.productParameter.key ? undefined : params.productParameter.key,
        productParameterValues: params.productParameterValues,
        notifications: []
    };
    const typeParam = newState.productParameters.find(it => it.key === 'types');
    const typeParamValueCurrent = typeParam.valueProvider.list.find(it => it.key === newState.product.configuration['types'][0].key);
    if (typeParamValueCurrent && typeParamValueCurrent.constraints) {
        return productBuilderParameterValueDisabledSet(newState, {
            productConfiguration: newState.product.configuration,
            productParameterValueConstraints: typeParamValueCurrent.constraints
        });
    } else {
        return newState;
    }
};

export const productBuilderParametersSet = (state: ProductBuilderState, params: { productParameters: ProductParameter[]; }): ProductBuilderState => ({
    ...state, productParameters: params.productParameters
});

export function* productBuilderParameterExpandRequest(action: { type: ProductBuilderAction, params: ProductParameter; }) {
    const values = yield call(productParameterValues, action.params.valueProvider);
    yield put({ type: 'PRODUCT_BUILDER_PARAMETER_EXPAND', params: { productParameter: action.params, productParameterValues: values } });
}

export function* productBuilderParameterExpandRequestedWatch() {
    yield takeEvery('PRODUCT_BUILDER_PARAMETER_EXPAND_REQUEST', productBuilderParameterExpandRequest);
}

export function productBuilderParameterValueDisableReset(state: ProductBuilderState): ProductBuilderState {
    var parameters = [...state.productParameters];
    parameters.filter(it => it.valueProvider && it.valueProvider.list).forEach(parameter => {
        parameter.disabled = false;
        parameter.valueProvider.list.forEach(value => {
            value.disabled = false;
        });
    });
    return {
        ...state,
        productParameters: parameters
    };
}

export function productBuilderParameterValueDisabledSet(
    state: ProductBuilderState,
    params: {
        productConfiguration: ProductConfiguration,
        productParameterValueConstraints: ProductParameterValueConstraints;
    }): ProductBuilderState {
    state = productBuilderParameterValueDisableReset(state);
    const parameters = [...state.productParameters];
    parameters.filter(it => it.valueProvider && it.valueProvider.list).forEach(parameter => {
        parameter.valueProvider.list.forEach(value => {
            value.disabled = false;
        });
    });
    if (!params.productParameterValueConstraints)
        return;
    const config = { ...params.productConfiguration };
    const productParameterValueConstraintKeys: string[] = [];
    params.productParameterValueConstraints.forEach(constraint => {
        for (let parameterKey in constraint) {
            if (productParameterValueConstraintKeys.indexOf(parameterKey) === -1)
                productParameterValueConstraintKeys.push(parameterKey);
        }
    });
    const constrainedParameters = parameters.filter(it => productParameterValueConstraintKeys.indexOf(it.key) !== -1);
    constrainedParameters.forEach((parameter, parameterIndex) => {
        parameter.valueProvider.list.forEach(value => {
            value.disabled = !params.productParameterValueConstraints.some(constraint => {
                return Object.keys(constraint).every(constraintKey => {
                    const constrainedParameterIndex = constrainedParameters.findIndex(it => it.key === constraintKey);
                    const constrainedParameter = constrainedParameters[constrainedParameterIndex];
                    if (constrainedParameter.key === parameter.key && !constraint[constraintKey].some(it => it === value.key))
                        return false;
                    const constrainedValues = constrainedParameter.valueProvider.list
                        .filter(v =>
                            constraintKey in config && config[constraintKey].length
                                ? config[constraintKey].some(v2 => v2.key === v.key)
                                : true);
                    //const expandedParameterIndex = constrainedParameters.findIndex(it => it.key === state.productParameterExpandedKey);
                    const valid =
                        constrainedParameterIndex >= parameterIndex
                        || constrainedValues.some(v =>
                            constraint[constraintKey].some(c => c === v.key));
                    return valid;
                });
            });
        });
    });
    for (let paramIndex = 0; paramIndex < parameters.length; paramIndex++) {
        const paramCurrent = parameters[paramIndex];
        const paramValueCurrent = state.product.configuration[paramCurrent.key];
        if (productParameterValueConstraintKeys.indexOf(paramCurrent.key) === -1)
            continue;
        const paramValueCurrentValid =
            paramValueCurrent
            && paramValueCurrent.length
            && paramValueCurrent.every(value => paramCurrent.valueProvider.list.some(
                availableValue => !availableValue.disabled && availableValue.key === value.key));
        paramCurrent.disabled = !paramValueCurrentValid;
        if (!paramValueCurrentValid) {
            paramCurrent.disabled = false;
            paramCurrent.changed = false;
            config[paramCurrent.key] = [];
        }
    }
    return {
        ...state,
        product: {
            ...state.product,
            configuration: config
        },
        productParameters: parameters
    };
}

export function* productBuilderParameterValueSelect(
    action: {
        type: ProductBuilderAction,
        params: {
            product: Product,
            productParameters: ProductParameter[],
            productParameterKey: string,
            productParameterValue: ProductParameterValue[],
            productParameterKeepOpen: boolean;
        };
    }) {
    const productConfiguration = { ...action.params.product.configuration };
    productConfiguration[action.params.productParameterKey] = action.params.productParameterValue;
    yield put({ type: 'PRODUCT_BUILDER_CONFIGURATION_SET', params: { productConfiguration, productParameterKeepOpen: action.params.productParameterKeepOpen } });
    yield put({ type: 'PRODUCT_BUILDER_COST_CALCULATE', params: { product: action.params.product, productConfiguration } });
    if (
        action.params.productParameterValue
        && action.params.productParameterValue.length === 1
        && action.params.productParameterValue[0].filesSelected) {
        yield put({
            type: 'PRODUCT_BUILDER_FILE_UPLOAD',
            params: {
                product: { ...action.params.product, configuration: productConfiguration },
                productParameterKey: action.params.productParameterKey,
                productParameterValueKey: action.params.productParameterValue[0].key,
                files: action.params.productParameterValue[0].filesSelected
            }
        });
    }
    const parameterSelected = action.params.productParameters.find(it => it.key === action.params.productParameterKey);
    if (!parameterSelected.multiple) {
        const typeParam = action.params.productParameters.find(it => it.key === 'types');
        if (productConfiguration['types']
            && productConfiguration['types'].length
            && typeParam
            && typeParam.valueProvider
            && typeParam.valueProvider.list
            && typeParam.valueProvider.list.length) {
            const typeParamValueCurrent = typeParam.valueProvider.list.find(it => it.key === productConfiguration['types'][0].key);
            if (typeParamValueCurrent && typeParamValueCurrent.constraints)
                yield put({ type: 'PRODUCT_BUILDER_PARAMETER_VALUE_DISABLED_SET', params: { productConfiguration, productParameterValueConstraints: typeParamValueCurrent.constraints } });
            else
                yield put({ type: 'PRODUCT_BUILDER_PARAMETER_VALUE_DISABLED_RESET' });
        } else {
            yield put({ type: 'PRODUCT_BUILDER_PARAMETER_VALUE_DISABLED_RESET' });
        }
    }

    yield put({ type: 'PRODUCT_BUILDER_PARAMETER_CHANGED', params: { productParameterKey: action.params.productParameterKey, productParameterChanged: true } });
}

export function* productBuilderParameterValueSelectWatch() {
    yield takeEvery('PRODUCT_BUILDER_PARAMETER_VALUE_SELECT', productBuilderParameterValueSelect);
}
